import { useEffect, useState } from "react";
import { PageHeader } from "@/components/Layout";
import api, { formatApiError } from "@/lib/api";
import { WO_STATUS, findMeta, eur } from "@/lib/constants";
import { ChevronLeft, ChevronRight, Inbox, CalendarDays, Users } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

function startOfWeek(d) {
  const date = new Date(d);
  const day = (date.getDay() + 6) % 7;
  date.setDate(date.getDate() - day);
  date.setHours(0, 0, 0, 0);
  return date;
}
function fmt(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function Card({ w, onDragStart, hideTech }) {
  const meta = findMeta(WO_STATUS, w.status);
  return (
    <div
      draggable={!w.locked}
      onDragStart={(e) => onDragStart(e, w)}
      data-testid={`cal-event-${w.id}`}
      className={`group mb-2 rounded-xl border-l-[3px] bg-white p-2.5 shadow-[0_1px_4px_rgba(0,0,0,0.05)] transition-all ${w.locked ? "opacity-70" : "cursor-grab hover:-translate-y-0.5 hover:shadow-md active:cursor-grabbing"}`}
      style={{ borderLeftColor: meta.color }}
    >
      <div className="flex items-center justify-between">
        <span className="font-mono text-[10px] text-[#86868B]">{w.number || "—"}</span>
        <span className="rounded-full px-1.5 py-0.5 text-[9px] font-medium" style={{ color: meta.color, backgroundColor: meta.bg }}>{meta.label}</span>
      </div>
      <p className="mt-0.5 truncate text-xs font-medium text-[#1D1D1F]">{w.title}</p>
      <p className="truncate text-[11px] text-[#86868B]">{w.client_name}</p>
      <div className="mt-1 flex items-center justify-between text-[10px] text-[#86868B]">
        {!hideTech && <span className="truncate">{w.technician_name || "Sin asignar"}</span>}
        <span className="ml-auto font-mono">{eur(w.amount)}</span>
      </div>
    </div>
  );
}

export default function Calendar() {
  const [wos, setWos] = useState([]);
  const [techs, setTechs] = useState([]);
  const [view, setView] = useState("week"); // week | tech
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [dragOver, setDragOver] = useState(null);

  const load = () => api.get("/work-orders").then((r) => setWos(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/technicians").then((r) => setTechs(r.data)).catch(() => {});
  }, []);

  const days = [...Array(7)].map((_, i) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    return d;
  });
  const todayStr = fmt(new Date());

  const onDragStart = (e, w) => {
    e.dataTransfer.setData("wo_id", w.id);
    e.dataTransfer.effectAllowed = "move";
  };
  const allowDrop = (e, key) => { e.preventDefault(); setDragOver(key); };

  // Week view drop: only reschedule
  const onDropDay = async (e, dateStr) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("wo_id");
    const w = wos.find((x) => x.id === id);
    if (!w || w.scheduled_date === dateStr) return;
    setWos((arr) => arr.map((x) => (x.id === id ? { ...x, scheduled_date: dateStr } : x)));
    try {
      await api.put(`/work-orders/${id}`, { scheduled_date: dateStr || "" });
      toast.success(dateStr ? "Trabajo reprogramado" : "Movido a Sin programar");
    } catch (err) { toast.error(formatApiError(err)); load(); }
  };

  // Swimlane drop: reassign technician + reschedule
  const onDropCell = async (e, techId, dateStr) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("wo_id");
    const w = wos.find((x) => x.id === id);
    if (!w) return;
    if ((w.technician_id || "") === techId && w.scheduled_date === dateStr) return;
    setWos((arr) => arr.map((x) => (x.id === id ? { ...x, technician_id: techId || null, technician_name: techId ? (techs.find((t) => t.id === techId)?.name) : null, scheduled_date: dateStr } : x)));
    try {
      await api.put(`/work-orders/${id}`, { technician_id: techId || null, scheduled_date: dateStr });
      toast.success("Trabajo reasignado y reprogramado");
    } catch (err) { toast.error(formatApiError(err)); load(); }
  };

  const byDate = {}, unscheduled = [];
  wos.forEach((w) => {
    if (w.scheduled_date) (byDate[w.scheduled_date] = byDate[w.scheduled_date] || []).push(w);
    else unscheduled.push(w);
  });

  const weekLabel = `${days[0].getDate()} ${days[0].toLocaleDateString("es-ES", { month: "short" })} – ${days[6].getDate()} ${days[6].toLocaleDateString("es-ES", { month: "short" })}`;

  const rows = [...techs.map((t) => ({ id: t.id, name: t.name })), { id: "", name: "Sin asignar" }];

  return (
    <div className="p-8 lg:p-12">
      <PageHeader
        title="Calendario"
        subtitle={view === "week" ? "Arrastra los trabajos entre días para reprogramarlos" : "Arrastra entre técnicos y días para reasignar y reprogramar"}
        action={
          <div className="flex items-center gap-3">
            <div className="flex rounded-full bg-[#F5F5F7] p-1">
              <button data-testid="cal-view-week" onClick={() => setView("week")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all ${view === "week" ? "bg-white text-[#0066CC] shadow-sm" : "text-[#86868B]"}`}><CalendarDays className="h-4 w-4" /> Semana</button>
              <button data-testid="cal-view-tech" onClick={() => setView("tech")} className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-medium transition-all ${view === "tech" ? "bg-white text-[#0066CC] shadow-sm" : "text-[#86868B]"}`}><Users className="h-4 w-4" /> Técnicos</button>
            </div>
            <button data-testid="cal-today" onClick={() => setWeekStart(startOfWeek(new Date()))} className="rounded-full border border-[#D2D2D7] bg-white px-4 py-2 text-sm font-medium transition-all hover:bg-[#F5F5F7]">Hoy</button>
            <button data-testid="cal-prev" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() - 7); setWeekStart(d); }} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D2D2D7] bg-white transition-all hover:bg-[#F5F5F7]"><ChevronLeft className="h-4 w-4" /></button>
            <span className="w-44 text-center font-display text-base font-medium text-[#1D1D1F]">{weekLabel}</span>
            <button data-testid="cal-next" onClick={() => { const d = new Date(weekStart); d.setDate(d.getDate() + 7); setWeekStart(d); }} className="flex h-9 w-9 items-center justify-center rounded-full border border-[#D2D2D7] bg-white transition-all hover:bg-[#F5F5F7]"><ChevronRight className="h-4 w-4" /></button>
          </div>
        }
      />

      {view === "week" ? (
        <div className="flex gap-4">
          <div
            data-testid="cal-backlog"
            onDragOver={(e) => allowDrop(e, "backlog")}
            onDragLeave={() => setDragOver(null)}
            onDrop={(e) => onDropDay(e, "")}
            className={`w-56 shrink-0 rounded-2xl border p-3 transition-colors ${dragOver === "backlog" ? "border-[#0066CC] bg-[#EAF2FC]" : "border-[#D2D2D7]/60 bg-white"}`}
          >
            <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-[#1D1D1F]">
              <Inbox className="h-4 w-4 text-[#86868B]" /> Sin programar
              <span className="ml-auto rounded-full bg-[#F5F5F7] px-2 text-xs text-[#86868B]">{unscheduled.length}</span>
            </div>
            <div className="min-h-[120px]">{unscheduled.map((w) => <Card key={w.id} w={w} onDragStart={onDragStart} />)}</div>
          </div>

          <div className="grid flex-1 grid-cols-7 gap-3">
            {days.map((d, i) => {
              const ds = fmt(d), events = byDate[ds] || [], isToday = ds === todayStr;
              const dayTotal = events.reduce((a, e) => a + (e.amount || 0), 0);
              return (
                <div key={ds} data-testid={`cal-day-${ds}`} onDragOver={(e) => allowDrop(e, ds)} onDragLeave={() => setDragOver(null)} onDrop={(e) => onDropDay(e, ds)}
                  className={`flex min-h-[420px] flex-col rounded-2xl border p-2 transition-colors ${dragOver === ds ? "border-[#0066CC] bg-[#EAF2FC]" : "border-[#D2D2D7]/50 bg-white"}`}>
                  <div className="mb-2 flex items-center justify-between px-1">
                    <div>
                      <p className="text-[10px] font-semibold uppercase tracking-wider text-[#86868B]">{DAYS[i]}</p>
                      <p className={`font-display text-lg font-bold ${isToday ? "text-[#0066CC]" : "text-[#1D1D1F]"}`}>{d.getDate()}</p>
                    </div>
                    {isToday && <span className="rounded-full bg-[#0066CC] px-2 py-0.5 text-[9px] font-semibold text-white">HOY</span>}
                  </div>
                  <div className="flex-1">{events.map((w) => <Card key={w.id} w={w} onDragStart={onDragStart} />)}</div>
                  {dayTotal > 0 && <div className="mt-1 border-t border-[#F5F5F7] pt-1 text-right font-mono text-[10px] text-[#86868B]">{eur(dayTotal)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-3xl border border-[#D2D2D7]/50 bg-white">
          <div className="min-w-[900px]">
            {/* header */}
            <div className="grid border-b border-[#D2D2D7]/50" style={{ gridTemplateColumns: "180px repeat(7, 1fr)" }}>
              <div className="p-3 text-xs font-semibold uppercase tracking-wider text-[#86868B]">Técnico</div>
              {days.map((d, i) => (
                <div key={i} className={`p-3 text-center ${fmt(d) === todayStr ? "bg-[#EAF2FC]" : ""}`}>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#86868B]">{DAYS[i]}</p>
                  <p className="font-display text-base font-bold text-[#1D1D1F]">{d.getDate()}</p>
                </div>
              ))}
            </div>
            {/* rows */}
            {rows.map((row) => (
              <div key={row.id || "none"} data-testid={`cal-lane-${row.id || "none"}`} className="grid border-b border-[#D2D2D7]/30 last:border-0" style={{ gridTemplateColumns: "180px repeat(7, 1fr)" }}>
                <div className="flex items-center gap-2 p-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold text-white ${row.id ? "bg-gradient-to-br from-[#0066CC] to-[#5856D6]" : "bg-[#C7C7CC]"}`}>{(row.name || "?").charAt(0)}</div>
                  <span className="truncate text-sm font-medium text-[#1D1D1F]">{row.name}</span>
                </div>
                {days.map((d, i) => {
                  const ds = fmt(d);
                  const key = `${row.id}|${ds}`;
                  const cell = wos.filter((w) => (w.technician_id || "") === row.id && w.scheduled_date === ds);
                  return (
                    <div key={i} data-testid={`cal-cell-${row.id || "none"}-${ds}`} onDragOver={(e) => allowDrop(e, key)} onDragLeave={() => setDragOver(null)} onDrop={(e) => onDropCell(e, row.id, ds)}
                      className={`min-h-[90px] border-l border-[#D2D2D7]/30 p-1.5 transition-colors ${dragOver === key ? "bg-[#EAF2FC]" : ""}`}>
                      {cell.map((w) => <Card key={w.id} w={w} onDragStart={onDragStart} hideTech />)}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
